// components/shop/ProductCardBit.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PriceTag from '../ui/PriceTag';
import LuxuryButton from '../ui/LuxuryButton';
import RatingStars from '../ui/RatingStars';
import { Reveal } from "../animations/Reveal";
import {
    FaHeart,
    FaRegHeart,
    FaEye,
    FaShoppingBag,
    FaLeaf,
    FaFlask,
    FaStar
} from 'react-icons/fa';
import '../../styles/globals.css';
import {Play} from "lucide-react";
import {designTokens} from "../../theme/designTokens";

const ProductCardBit = ({
                            product,
                            index,
                            onAddToCart,
                            onViewProduct,
                            isLoading = false,
                            showQuickView = true,
                            showWishlist = true
                        }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isWishlisted, setIsWishlisted] = useState(false);
    const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const navigate = useNavigate();

    // Handle image rotation on hover
    useEffect(() => {
        let interval;
        if (isHovered && product.images && product.images.length > 1) {
            interval = setInterval(() => {
                setCurrentImageIndex(prev =>
                    (prev + 1) % product.images.length
                );
            }, 3000); // Change every 3 seconds
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isHovered, product.images]);

    // Reset image index when hover ends
    useEffect(() => {
        if (!isHovered) {
            setCurrentImageIndex(0);
        }
    }, [isHovered]);

    const cardVariants = {
        hidden: { opacity: 0, y: 40, scale: 0.95 },
        visible: (i) => ({
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: i * 0.1,
                ease: [0.4, 0, 0.2, 1]
            }
        })
    };

    // Get display image
    const getDisplayImage = () => {
        if (product.images && product.images.length > 0) {
            return product.images[currentImageIndex];
        }
        return null;
    };

    // Get display price
    const getDisplayPrice = () => {
        return product.price || 0;
    };

    // Get display original price
    const getDisplayOriginalPrice = () => {
        return product.originalPrice || product.price || 0;
    };

    // Check if product has discount
    const hasDiscount = () => {
        const originalPrice = getDisplayOriginalPrice();
        const price = getDisplayPrice();
        return originalPrice > price;
    };

    // Calculate discount percentage
    const getDiscountPercentage = () => {
        if (product.discount) return product.discount;

        const originalPrice = getDisplayOriginalPrice();
        const price = getDisplayPrice();
        if (originalPrice > price) {
            return Math.round(((originalPrice - price) / originalPrice) * 100);
        }
        return 0;
    };

    // Calculate savings amount
    const getSavingsAmount = () => {
        const originalPrice = getDisplayOriginalPrice();
        const price = getDisplayPrice();
        if (originalPrice > price) {
            return (originalPrice - price).toFixed(2);
        }
        return 0;
    };

    const handleCardClick = () => {
        if (onViewProduct) {
            onViewProduct(product);
        } else {
            navigate(`/product/${product.id || product._id}`);
        }
    };

    const handleWishlistToggle = (e) => {
        e.stopPropagation();
        setIsWishlisted(!isWishlisted);
        // TODO: Implement wishlist API call
    };

    const handleAddToCartClick = async (e, variantId = 'default') => {
        e.stopPropagation();
        try {
            if (onAddToCart) {
                await onAddToCart(e, variantId);
            }
        } catch (error) {
            console.error('Failed to add to cart:', error);
        }
    };

    const handleQuickView = (e) => {
        e.stopPropagation();
        setIsQuickViewOpen(true);
    };

    const handleImageClick = (e) => {
        e.stopPropagation();
        handleCardClick();
    };

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    // Get first variant for default add to cart
    const getFirstVariant = () => {
        return product.variants?.[0] || null;
    };

    // Check if product is in stock
    const isInStock = product.inStock !== false;

    return (
        <Reveal delay={index * 0.05}>
            <motion.div
                variants={cardVariants}
                custom={index}
                initial="hidden"
                animate="visible"
                whileHover={{ y: -8 }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className="group relative cursor-pointer h-full"
                onClick={handleCardClick}
            >
                {/* Product Card Container */}
                <div className="relative bg-white rounded-2xl overflow-hidden shadow-subtle hover:shadow-lift transition-all duration-500 flex flex-col h-full border border-nude/20">
                    {/* Product Image Container */}
                    <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-nude/5 to-pearl/10">
                        {getDisplayImage() ? (
                            <>
                                <motion.img
                                    key={currentImageIndex}
                                    src={getDisplayImage()}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                    animate={{ scale: isHovered ? 1.05 : 1 }}
                                    transition={{ duration: 0.6, ease: "easeOut" }}
                                    onClick={handleImageClick}
                                />

                                {/* Image Navigation Dots */}
                                {product.images && product.images.length > 1 && (
                                    <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-1.5">
                                        {product.images.map((_, idx) => (
                                            <button
                                                key={idx}
                                                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                                                    idx === currentImageIndex
                                                        ? 'bg-white w-4'
                                                        : 'bg-white/50'
                                                }`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCurrentImageIndex(idx);
                                                }}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Quick View Overlay */}
                                {isHovered && (
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent flex items-center justify-center">
                                        <span className="text-white text-sm font-medium bg-black/40 backdrop-blur-sm px-4 py-2 rounded-full">
                                            Quick View
                                        </span>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-nude/10 to-pearl/20">
                                <span className="text-charcoal/30">No Image Available</span>
                            </div>
                        )}

                        {/* Badges */}
                        <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
                            {hasDiscount() && (
                                <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="px-2.5 py-1 bg-champagne text-charcoal text-xs font-medium rounded-full shadow-sm"
                                >
                                    -{getDiscountPercentage()}%
                                </motion.span>
                            )}

                            {product.isNew && (
                                <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.1 }}
                                    className="px-2.5 py-1 bg-sage text-white text-xs font-medium rounded-full shadow-sm"
                                >
                                    New
                                </motion.span>
                            )}

                            {product.isBestseller && (
                                <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.2 }}
                                    className="px-2.5 py-1 bg-charcoal text-white text-xs font-medium rounded-full shadow-sm"
                                >
                                    Best Seller
                                </motion.span>
                            )}

                            {!isInStock && (
                                <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.3 }}
                                    className="px-2.5 py-1 bg-charcoal/60 text-white text-xs font-medium rounded-full shadow-sm"
                                >
                                    Out of Stock
                                </motion.span>
                            )}
                        </div>

                        {/* Quick Actions */}
                        {isHovered && (
                            <motion.div
                                className="absolute top-3 right-3 flex flex-col gap-2 z-10"
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                {showWishlist && (
                                    <motion.button
                                        onClick={handleWishlistToggle}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm"
                                    >
                                        {isWishlisted ? (
                                            <FaHeart className="w-3.5 h-3.5 text-champagne fill-current" />
                                        ) : (
                                            <FaRegHeart className="w-3.5 h-3.5 text-charcoal" />
                                        )}
                                    </motion.button>
                                )}

                                {showQuickView && (
                                    <motion.button
                                        onClick={handleQuickView}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm"
                                    >
                                        <FaEye className="w-3.5 h-3.5 text-charcoal" />
                                    </motion.button>
                                )}
                            </motion.div>
                        )}
                    </div>

                    {/* Product Info Container */}
                    <div className="flex-1 p-4 flex flex-col">
                        {/* Category & Icons */}
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-sage uppercase tracking-wider">
                                {product.category || 'Skincare'}
                            </span>
                            <div className="flex items-center gap-1">
                                {product.freeFrom?.includes('Artificial Fragrance') && (
                                    <FaLeaf className="w-3 h-3 text-sage" title="No Artificial Fragrance" />
                                )}
                                {product.ingredients?.some(ing => ing.includes('Vitamin')) && (
                                    <FaFlask className="w-3 h-3 text-champagne" title="Vitamin Enriched" />
                                )}
                            </div>
                        </div>

                        {/* Product Name */}
                        <h3 className="text-base font-medium text-charcoal mb-2 line-clamp-1 group-hover:text-champagne transition-colors">
                            {product.name}
                        </h3>

                        {/* Description */}
                        <p className="text-sm text-charcoal/60 mb-3 line-clamp-2 flex-1">
                            {product.description || 'Premium skincare product'}
                        </p>

                        {/* Rating */}
                        <div className="flex items-center gap-2 mb-3">
                            <RatingStars
                                rating={product.rating || 0}
                                size="small"
                                showNumber={true}
                            />
                            <span className="text-xs text-charcoal/40">
                                ({product.reviewCount || 0})
                            </span>
                        </div>

                        {/* Price Section */}
                        <div className="mb-3">
                            <PriceTag
                                price={getDisplayPrice()}
                                originalPrice={getDisplayOriginalPrice()}
                                discount={getDiscountPercentage()}
                                size="small"
                                showSavings={hasDiscount()}
                                savingsAmount={getSavingsAmount()}
                            />
                        </div>

                        {/* Concerns/Tags */}
                        {product.concerns && product.concerns.length > 0 && (
                            <div className="mb-3">
                                <div className="flex flex-wrap gap-1.5">
                                    {product.concerns.slice(0, 2).map((concern, idx) => (
                                        <span
                                            key={idx}
                                            className="px-2 py-1 bg-nude/20 text-charcoal/70 text-xs rounded-full"
                                        >
                                            {concern}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Add to Cart Button */}
                        <div className="mt-auto pt-3 border-t border-nude/30">
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-full"
                            >

                                <LuxuryButton
                                    onClick={(e) => handleAddToCartClick(e, getFirstVariant()?.id)}
                                    icon={FaShoppingBag}
                                    iconPosition="left"
                                    loading={isLoading}
                                    disabled={isLoading || !isInStock}
                                    variant="primary"
                                    style={{
                                        background: designTokens.colors.gradients.champagne,
                                    }}
                                >
                                    {isLoading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                            Adding...
                                        </span>
                                    ) : !isInStock ? (
                                        'Out of Stock'
                                    ) : (
                                        'Add to Cart'
                                    )}
                                </LuxuryButton>
                            </motion.div>
                        </div>
                    </div>

                    {/* Hover Glow Effect */}
                    {isHovered && (
                        <motion.div
                            className="absolute inset-0 border-2 border-champagne/30 rounded-2xl pointer-events-none"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                        />
                    )}
                </div>

                {/* Quick View Modal */}
                <AnimatePresence>
                    {isQuickViewOpen && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsQuickViewOpen(false)}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        >
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-auto shadow-xl"
                            >
                                <div className="p-6">
                                    <div className="flex justify-between items-start mb-6">
                                        <h2 className="text-2xl font-medium text-charcoal">{product.name}</h2>
                                        <button
                                            onClick={() => setIsQuickViewOpen(false)}
                                            className="text-charcoal/40 hover:text-charcoal transition-colors"
                                        >
                                            ✕
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            {product.images?.[0] ? (
                                                <img
                                                    src={product.images[0]}
                                                    alt={product.name}
                                                    className="w-full h-auto rounded-xl shadow-subtle"
                                                />
                                            ) : (
                                                <div className="w-full h-48 bg-nude/20 rounded-xl flex items-center justify-center">
                                                    <span className="text-charcoal/30">No image available</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="space-y-4">
                                            <p className="text-charcoal/70">
                                                {product.description || product.detailedDescription}
                                            </p>

                                            <div>
                                                <PriceTag
                                                    price={getDisplayPrice()}
                                                    originalPrice={getDisplayOriginalPrice()}
                                                    discount={getDiscountPercentage()}
                                                    size="large"
                                                />
                                            </div>

                                            {product.benefits && product.benefits.length > 0 && (
                                                <div>
                                                    <h4 className="font-medium text-charcoal mb-2">Key Benefits</h4>
                                                    <div className="space-y-2">
                                                        {product.benefits.slice(0, 3).map((benefit, idx) => (
                                                            <div key={idx} className="flex items-start gap-2">
                                                                <div className="w-5 h-5 rounded-full bg-champagne/10 flex items-center justify-center mt-0.5 flex-shrink-0">
                                                                    <span className="text-xs text-champagne">✓</span>
                                                                </div>
                                                                <span className="text-sm text-charcoal/70">
                                                                    {benefit.title}: {benefit.description}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {product.variants && product.variants.length > 1 && (
                                                <div>
                                                    <h4 className="font-medium text-charcoal mb-2">Available Sizes</h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {product.variants.map((variant, idx) => (
                                                            <button
                                                                key={idx}
                                                                onClick={(e) => handleAddToCartClick(e, variant.id)}
                                                                className="px-3 py-2 border border-nude rounded-lg hover:border-champagne hover:text-champagne transition-colors"
                                                            >
                                                                {variant.name} - ${variant.price}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex gap-3 pt-4">
                                                <LuxuryButton
                                                    size="medium"
                                                    onClick={(e) => {
                                                        handleAddToCartClick(e, getFirstVariant()?.id);
                                                        setIsQuickViewOpen(false);
                                                    }}
                                                    icon={FaShoppingBag}
                                                    loading={isLoading}
                                                    disabled={isLoading || !isInStock}
                                                    className="flex-1"
                                                >
                                                    {isLoading ? 'Adding...' : 'Add to Cart'}
                                                </LuxuryButton>

                                                <LuxuryButton
                                                    size="medium"
                                                    variant="outline"
                                                    onClick={() => {
                                                        setIsQuickViewOpen(false);
                                                        handleCardClick();
                                                    }}
                                                    className="flex-1"
                                                >
                                                    View Full Details
                                                </LuxuryButton>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </Reveal>
    );
};

// Default props
ProductCardBit.defaultProps = {
    showQuickView: true,
    showWishlist: true
};

export default ProductCardBit;